import { GetServerSidePropsContext } from 'next';
import { Amplify, withSSRContext, Auth } from 'aws-amplify';
import {
  TypeImage,
  TypeProductFields,
  TypeProduct,
} from '@/core/generated-types';
import {
  getContentType,
  CONTENT_TYPE_PAGE,
  LEGACY_CONTENT_TYPE_COLLECTION_PAGE,
  CONTENT_TYPE_PRODUCT_PAGE,
} from '@/core/constants';
import { isServerSide } from '@/utils/environment';
import { setCookie } from '@/utils/cookies';
import { parse } from 'cookie';
import { InvoiceItem } from '@/components/checkout/OrderSummary/type';
import { UserSubscriptions } from '@/context/SubscriptionContext';
import { AuthState } from '@/context/AuthContext';
import { ShopifyVariant } from '@/lib/shopify';
import { amplifyConfiguration } from '@/core/amplify';
import { ReadonlyHeaders } from 'next/dist/server/web/spec-extension/adapters/headers';

type AuthClass = typeof Auth;

export const importLoveveryReact = async () => {
  return import('@lovevery-digital/lovevery-react');
};

export function getSSRAuth(context: GetServerSidePropsContext): AuthClass {
  const { Auth } = withSSRContext(context);
  return Auth;
}

export function consoleErrorFiltered(error: any) {
  if (error !== 'The user is not authenticated') {
    console.error(error);
  } else {
    console.log(error);
  }
}

export async function getSSRAuthFromCookies(headers: () => ReadonlyHeaders) {
  const req = {
    headers: {
      cookie: headers().get('cookie'),
    },
  };

  try {
    Amplify.configure(amplifyConfiguration);
    const { Auth } = withSSRContext({ req });
    const user = await Auth.currentAuthenticatedUser();
    const idToken = user?.signInUserSession?.idToken;
    const userUuid = idToken.payload['custom:account_manager_id'];
    const userName = idToken.payload.name;
    const authToken = idToken?.jwtToken;

    const authenticatedUser: ServerAuthenticatedUser = {
      authToken,
      userUuid,
      userName,
    };

    return authenticatedUser;
  } catch (error) {
    // An unauthenticated user results in an error.
    consoleErrorFiltered(error);

    return null;
  }
}

export async function getAuthTokenServer(context: GetServerSidePropsContext) {
  try {
    const Auth = getSSRAuth(context);
    const user = await Auth.currentAuthenticatedUser();
    return user?.signInUserSession?.idToken?.jwtToken;
  } catch (error) {
    // An unauthenticated user results in an error.
    consoleErrorFiltered(error);
  }
}

export type AuthenticatedUser = {
  authToken: string | undefined;
  userUuid: string | undefined;
  userName: string | undefined;
};

export type ServerAuthenticatedUser = {
  authToken: string;
  userUuid: string;
  userName: string;
};

export async function getSSRSafeAuthenticatedUser(
  context: GetServerSidePropsContext,
) {
  try {
    const Auth = getSSRAuth(context);
    const user = await Auth.currentAuthenticatedUser();
    const idToken = user?.signInUserSession?.idToken;
    const userUuid = idToken.payload['custom:account_manager_id'];
    const userName = idToken.payload.name;
    const authToken = idToken?.jwtToken;

    return {
      authToken,
      userUuid,
      userName,
    } as AuthenticatedUser;
  } catch (error) {
    // An unauthenticated user results in an error.
    consoleErrorFiltered(error);

    return {
      authToken: undefined,
      userUuid: undefined,
      userName: undefined,
    } as AuthenticatedUser;
  }
}

export async function getAuthTokenClient() {
  try {
    const { getUserJwt } = await importLoveveryReact();
    return await getUserJwt();
  } catch (error) {
    consoleErrorFiltered(error);
  }
}

export function redirectTo(destinationUrl?: string) {
  const destination = destinationUrl;

  return {
    redirect: {
      permanent: false,
      destination,
    },
  };
}

export function redirectToLoginPage(returnUrl?: string) {
  const loginPath = '/account/login';
  const destination = returnUrl
    ? `${loginPath}?return_url=${encodeURI(returnUrl)}`
    : loginPath;

  return {
    redirect: {
      permanent: false,
      destination,
    },
  };
}

export function redirectToAccountHomePage() {
  const legacyHost = process.env.BACKEND_DOMAIN || 'lovevery.com';
  const destination = `https://shop.${legacyHost}/account`;

  return {
    redirect: {
      permanent: false,
      basePath: false,
      destination,
    },
  };
}

export const isObjectEmpty = (objectName: object) => {
  return (
    objectName &&
    Object.keys(objectName).length === 0 &&
    objectName.constructor === Object
  );
};

export const loadScript = (
  url: string,
  id: string,
  callback?: () => void,
): void => {
  if (typeof window === 'undefined') return;

  const existingScript = window.document.querySelector(`#${id}`);
  if (!existingScript) {
    const script = window.document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = url;
    script.id = id;
    if (typeof callback === 'function') {
      script.onload = callback;
    }
    window.document.head.insertAdjacentElement('beforeend', script);
  }
  if (existingScript && callback) callback();
};

// @TODO - dynamically pull locale and currency values
export const priceFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
});

export const priceFormatterDecimals = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

export const formatImageObject = (
  altText: string | undefined,
  url: string | undefined,
) => {
  return {
    fields: {
      altText: altText || '',
      Image: {
        fields: {
          file: { url: url || '' },
        },
      },
    },
  } as TypeImage;
};

export const capitalizeFirstLetter = (string: string) => {
  if (!string) {
    return string;
  }

  return `${string.substring(0, 1).toUpperCase()}${string.substring(1)}`;
};

export const isValidFreeformDateFormat = (dateFormat: string[]) => {
  const isValid =
    !!dateFormat.find((element) => element === 'year') &&
    !!dateFormat.find((element) => element === 'month') &&
    !!dateFormat.find((element) => element === 'day');

  if (!isValid) {
    console.warn(
      `Invalid date format detected. Array should include a 'year', 'month', and 'day' element.`,
      dateFormat,
    );
  }

  return isValid;
};

export const freeformDateFormatHelper = (dateFormat: string) => {
  return isValidFreeformDateFormat(dateFormat.split('/'))
    ? dateFormat.split('/')
    : ['month', 'day', 'year'];
};

export function base64Encode(str: string): string {
  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    return window.btoa(str);
  } else if (typeof Buffer !== 'undefined') {
    return Buffer.from(str).toString('base64');
  }
  return str; // Fallback for unsupported environments
}

export function decodeProductId(base64Str: string | undefined): string {
  if (!base64Str) return '';

  let decodedStr: string;

  if (typeof window !== 'undefined' && typeof window.atob === 'function') {
    decodedStr = window.atob(base64Str);
  } else if (typeof Buffer !== 'undefined') {
    decodedStr = Buffer.from(base64Str, 'base64').toString();
  } else {
    return base64Str;
  }

  return extractIdFromShopifyStr(decodedStr);
}

/*
  Returns an ID from a Shopify ID string, ex: "40119209951255"
  from "gid://shopify/ProductVariant/40119209951255"
*/
export function extractIdFromShopifyStr(str: string): string {
  const numberStartIndex = str.lastIndexOf('/') + 1;
  return str.substring(numberStartIndex);
}

export function toProductVariantGid(
  variantId?: string | number | null,
): string {
  if (!variantId) return '';
  const id = String(variantId);
  const prefix = 'gid://shopify/ProductVariant/';
  return id.startsWith(prefix) ? id : `${prefix}${id}`;
}

export function truncate(input: string, length: number) {
  if (input.length <= length) return input;
  const result = input.slice(0, length);
  const lastSpaceIndex = result.lastIndexOf(' ');
  return `${result.slice(0, lastSpaceIndex)}...`;
}

export function getPagePath(page: any) {
  const id = getContentType(page);

  if (id === CONTENT_TYPE_PAGE.PAGE) {
    return 'pages';
  }

  if (id === LEGACY_CONTENT_TYPE_COLLECTION_PAGE.LEGACY_COLLECTION_PAGE) {
    return 'collections';
  }

  if (
    id === CONTENT_TYPE_PRODUCT_PAGE.PLAY_KITS_LANDING_PAGE ||
    id === CONTENT_TYPE_PRODUCT_PAGE.PLAY_KIT_PAGE ||
    id === CONTENT_TYPE_PRODUCT_PAGE.PRODUCT_LANDING_PAGE ||
    id === CONTENT_TYPE_PRODUCT_PAGE.PRODUCT_PAGE ||
    id === CONTENT_TYPE_PRODUCT_PAGE.AGE_BASED_PLAY_KITS_LANDING_PAGE
  ) {
    return 'products';
  }

  return '';
}

export function getLocaleDomain(locale?: string): string {
  switch (locale) {
    case 'en-US':
      return 'https://lovevery.com';
    case 'en-AU':
      return 'https://lovevery.com.au';
    case 'en-GB':
      return 'https://lovevery.co.uk';
    case 'en-EU':
      return 'https://lovevery.eu';
    case 'nl':
      return 'https://lovevery.eu/nl';
    case 'fr':
      return 'https://lovevery.eu/fr';
    case 'de':
      return 'https://lovevery.eu/de';
    case 'es':
      return 'https://lovevery.eu/es';
    default:
      '';
  }

  return '';
}

export function getPlatform(): string {
  if (!isServerSide()) {
    const width = window.innerWidth;
    if (width < 768) {
      return 'mobile';
    } else if (width < 1024) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  }
  return '';
}

export function shouldUseYotpoReviews() {
  return ['US', 'AU'].includes(process.env.NEXT_PUBLIC_REGION as string);
}

export function shouldUseTrustedShopsReviews() {
  return ['UK', 'EU'].includes(process.env.NEXT_PUBLIC_REGION as string);
}
/**
 * Recursively filters out unnecessary properties from a Contentful object
 * preserving only essential information like id, contentType, and fields
 * @param obj - The Contentful object to filter.
 * @returns The filtered Contentful object.
 */
export function filterContentfulObject(obj: any): any {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  const { sys, fields } = obj;

  if (sys === undefined || fields === undefined) {
    return obj;
  }

  const filteredObj: any = {
    sys: {
      id: sys.id,
    },
    fields,
  };

  if (sys.contentType?.sys?.id) {
    filteredObj.sys.contentType = {
      sys: {
        id: sys.contentType.sys.id,
      },
    };
  }

  const filteredObjFields = filteredObj.fields;

  for (const key in filteredObjFields) {
    const value = filteredObjFields[key];

    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        const filteredArray = [];

        for (const item of value) {
          filteredArray.push(filterContentfulObject(item));
        }

        filteredObjFields[key] = filteredArray;
      } else {
        filteredObjFields[key] = filterContentfulObject(value);
      }
    }
  }

  return filteredObj;
}

export interface LocaleInfo {
  browserLocale: string;
  currencyCode: string;
  language: string;
  locale?: string;
}

export const getLocaleInfo = (locale?: string): LocaleInfo => {
  switch (locale) {
    case 'fr':
      return {
        browserLocale: 'fr-FR',
        currencyCode: 'EUR',
        language: 'fr',
        locale,
      };
    case 'de':
      return {
        browserLocale: 'de-DE',
        currencyCode: 'EUR',
        language: 'de',
        locale,
      };
    case 'nl':
      return {
        browserLocale: 'nl-NL',
        currencyCode: 'EUR',
        language: 'nl',
        locale,
      };
    case 'es':
      return {
        browserLocale: 'es-ES',
        currencyCode: 'EUR',
        language: 'es',
        locale,
      };
    case 'en-GB':
      return {
        browserLocale: 'en-GB',
        currencyCode: 'GBP',
        language: 'en-GB',
        locale,
      };
    case 'en-AU':
      return {
        browserLocale: 'en-AU',
        currencyCode: 'AUD',
        language: 'en',
        locale,
      };
    case 'en-EU':
      return {
        browserLocale: 'en-EU',
        currencyCode: 'EUR',
        language: 'en',
        locale,
      };
    case 'en-US':
    default:
      return {
        browserLocale: 'en-US',
        currencyCode: 'USD',
        language: 'en',
        locale,
      };
  }
};

export const formatMoney = (
  amount: number,
  locale?: string,
  hideDecimals?: boolean,
  showDecimalsToFloat = false,
) => {
  const localeInfo = getLocaleInfo(locale);
  const isInteger = showDecimalsToFloat && Number.isInteger(amount);

  let options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: localeInfo.currencyCode,
  };

  if (hideDecimals || isInteger) {
    options = {
      ...options,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    };
  }

  return amount.toLocaleString(localeInfo.browserLocale, options);
};

/**
 * Formats a number to show cents only when they're meaningful (not .00 or .0)
 * @param amount - The amount in dollars (not cents)
 * @param locale - The locale for currency formatting
 * @returns Formatted string with cents only when needed
 */
export const formatMoneyWithSmartDecimals = (
  amount: number | string,
  locale?: string,
): string => {
  const isAUStore = process.env.NEXT_PUBLIC_REGION === 'AU';
  const localeInfo = getLocaleInfo(locale);

  // Ensure amount is a number (Shopify sometimes returns price as string e.g. "160.0")
  const numericAmount =
    typeof amount === 'string' ? parseFloat(amount) : amount;

  // Handle NaN case
  if (isNaN(numericAmount)) {
    return '';
  }

  // Determine the appropriate number of decimal places
  let fractionDigits = 2;

  // Check if amount is a whole number
  if (Math.floor(numericAmount) === numericAmount) {
    fractionDigits = 0;
  }
  // Check if amount ends in .5 or .50
  else if (numericAmount % 1 === 0.5) {
    // Round to avoid floating point precision issues
    const cents = Math.round(numericAmount * 100) % 10;
    // It's .50, show 2 decimals; it's .5, show 1 decimal
    fractionDigits = cents === 0 ? 2 : 1;
  }

  // Format the amount once with the determined decimal places
  const formattedAmount = numericAmount.toLocaleString(
    localeInfo.browserLocale,
    {
      style: 'currency',
      currency: localeInfo.currencyCode,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    },
  );

  // For AU stores, prepend "AUD" to the formatted amount
  return isAUStore ? `AUD ${formattedAmount}` : formattedAmount;
};

export const formatReferralCredits = (amount: number, locale?: string) => {
  const localeInfo = getLocaleInfo(locale);
  const amountInCents = amount / 100;

  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: localeInfo.currencyCode,
  };

  return amountInCents.toLocaleString(localeInfo.browserLocale, options);
};

export const getMaxAge = (features: Record<string, boolean>) => {
  if (features.subflow_v2_enable_4yo_schedule) {
    return 5;
  } else if (features.subflow_v2_enable_3yo_schedule) {
    return 4;
  } else {
    return 2;
  }
};

export const convertIntObj = (
  obj: Record<string, string | number>,
  exemptKeys: string[] = [],
) => {
  const res: Record<string, string | number> = {};
  for (const key in obj) {
    const parsed = parseInt(obj[key] as string, 10);
    res[key] = isNaN(parsed) || exemptKeys.includes(key) ? obj[key] : parsed;
  }
  return res;
};

export const orderByScheduleDate = (items: any) => {
  return items
    .slice()
    .sort((a: any, b: any) => a.scheduled_date - b.scheduled_date);
};

export const getFirstParagraph = (description_html?: string) => {
  if (!description_html) return '';
  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(description_html, 'text/html');
  const firstParagraph = htmlDoc.getElementsByTagName('P')[0]?.textContent;

  return firstParagraph?.split('.')[0] || '';
};

export function addLineBreaksAroundParentheses(chargeName: string) {
  return chargeName.replace(/\(([^)]+)\)/g, '\n($1)\n');
}

export const setCustomCookie = (
  context: GetServerSidePropsContext,
  key: string,
  value: string,
) => {
  setCookie(context.res, key, value, {
    httpOnly: false,
    secure: false,
    maxAge: 60 * 60 * 24, // 1 day
    path: '/',
  });
};

export const getCustomCookie = (
  context: GetServerSidePropsContext,
  key: string,
) => {
  const cookies = parse(context.req.headers.cookie || '');
  return cookies[key];
};

export const extractKitMonth = (invoice_item: InvoiceItem) => {
  const kitMonthFound = invoice_item?.localized_description.match(/\d+/g);
  return kitMonthFound ? kitMonthFound[0] : null;
};

export enum ProductValidation {
  loggedIn = 'logged in',
  activeSubscriber = 'active subscriber',
  agedOutSubscriber = 'aged-out subscriber',
  noValidate = 'no validate',
}

export const isProductEnabled = (
  validations: TypeProductFields['validations'] = [],
  subscriptions: UserSubscriptions = {},
  user?: AuthState | null,
) => {
  // Product is always enabled when validations are missing
  if (validations.length === 0) return true;
  if (validations.includes(ProductValidation.noValidate)) return true;

  const { isActiveSubscriber, isAgedOutSubscriber } = subscriptions;

  // Product validations work with or condition, which means that
  // when any of them is true, the product should not be disabled
  const conditions = {
    [ProductValidation.loggedIn]: user,
    [ProductValidation.activeSubscriber]: isActiveSubscriber,
    [ProductValidation.agedOutSubscriber]: isAgedOutSubscriber,
  };

  return validations.some((validation) => {
    return (conditions as any)[validation];
  });
};

export const getVariantImageSet = (
  product?: TypeProduct | null,
  variantId?: string | null,
) => {
  if (!product?.fields.variantImageSets?.length) return [];

  if (!variantId) return product.fields.variantImageSets[0].fields.images;

  return product.fields.variantImageSets.find(
    (imageSet) => imageSet.fields.variantId === variantId,
  )?.fields.images;
};

export const getVariantId = (variant?: ShopifyVariant) => {
  return variant?.id.replace('gid://shopify/ProductVariant/', '');
};
